import type { JSX } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Copy, Search } from 'lucide-react'
import type { AuditEntryDto, AuditQuery } from '@/lib/dto'
import { cn } from '@/lib/cn'
import { useAudit } from '@/hooks/queries'
import { toast } from '@/stores/toasts'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { Select } from '@/ui/primitives/Select'
import { StatusDot } from '@/ui/primitives/StatusDot'
import { Panel, SectionHead } from '@/ui/primitives/Surface'
import { clockOf, dateTimeOf } from '@/ui/format'
import { EmptyBlock, ErrorBlock } from '@/screens/shared/states'
import {
  actorFilterToQuery,
  actorOptions,
  auditActionLabel,
  auditActionOptions,
  auditActionTone,
  auditActor,
  isKnownAuditAction,
  targetTypeLabel,
  targetTypeOptions
} from './audit-actions'

/**
 * Экран «Аудит» — журнал событий безопасности организации (docs/06 §7, FR-A9).
 *
 * Решения, которые здесь важны:
 *
 *  1. Действие показывается ДВАЖДЫ: человеческим названием и сырым кодом мелким
 *     моно-шрифтом. Название нужно тому, кто разбирается в инциденте; код — тому,
 *     кто пойдёт грепать его в логах сервера. Выбор «или-или» проигрывает обоим.
 *  2. Фильтры уходят НА СЕРВЕР (все, включая актора и вид объекта): аудит живёт
 *     365 дней, фильтровать его на клиенте бессмысленно — до нужной записи
 *     страницы просто не докрутить.
 *  3. Пагинация курсорная, «показать ещё» доклеивает страницу к загруженным.
 *     Это честнее нумерации: в аудит постоянно пишут, и «страница 7» через минуту
 *     означает другие записи.
 *  4. Сортировка по времени переворачивает ЗАГРУЖЕННЫЕ записи (контракт
 *     `GET /audit` отдаёт только `at desc`). Про это прямо написано под таблицей —
 *     тихо соврать про порядок хуже, чем не дать сортировку вовсе.
 */

export interface Filters {
  action: string
  /** `u:<id>` / `k:<id>` — см. `actorFilterToQuery`. */
  actor: string
  targetType: string
  targetId: string
  /** `YYYY-MM-DD` из `<input type="date">`. */
  from: string
  to: string
}

const EMPTY_FILTERS: Filters = {
  action: '',
  actor: '',
  targetType: '',
  targetId: '',
  from: '',
  to: ''
}

const PAGE_LIMIT = 50

export function AuditScreen(): JSX.Element {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  // Поле «объект» набирают руками, и каждый символ не должен уходить запросом.
  const [targetIdDraft, setTargetIdDraft] = useState('')
  const [sortDesc, setSortDesc] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    const timer = globalThis.setTimeout(
      () => setFilters(previous => ({ ...previous, targetId: targetIdDraft.trim() })),
      350
    )
    return () => globalThis.clearTimeout(timer)
  }, [targetIdDraft])

  const query = useMemo((): AuditQuery => toQuery(filters), [filters])
  const audit = useAudit(query)

  const rows = useMemo(() => audit.data?.pages.flatMap(page => page.items) ?? [], [audit.data])
  const sorted = useMemo(() => (sortDesc ? rows : [...rows].reverse()), [rows, sortDesc])

  const filtersActive = Object.values(filters).some(value => value !== '')

  function reset(): void {
    setFilters(EMPTY_FILTERS)
    setTargetIdDraft('')
  }

  return (
    <>
      <SectionHead
        actions={
          <>
            <Chip mono title="Срок хранения записей аудита">
              365 дней
            </Chip>
            <Button variant="ghost" size="sm" disabled={!filtersActive} onClick={reset}>
              Сбросить фильтры
            </Button>
          </>
        }
      />

      <Panel
        title="События безопасности"
        note={`${rows.length} записей загружено`}
        bodyClassName="p-0"
        actions={
          <>
            <Select
              label="Действие"
              hideLabel
              value={filters.action}
              onValueChange={value => setFilters(previous => ({ ...previous, action: value }))}
              options={auditActionOptions(rows.map(entry => entry.action))}
              className="w-64"
            />
            <Select
              label="Актор"
              hideLabel
              value={filters.actor}
              onValueChange={value => setFilters(previous => ({ ...previous, actor: value }))}
              options={actorOptions(rows)}
              className="w-44"
            />
            <Select
              label="Вид объекта"
              hideLabel
              value={filters.targetType}
              onValueChange={value => setFilters(previous => ({ ...previous, targetType: value }))}
              options={targetTypeOptions(rows.map(entry => entry.targetType))}
              className="w-40"
            />
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-faint"
              />
              <input
                type="search"
                value={targetIdDraft}
                onChange={event => setTargetIdDraft(event.target.value)}
                aria-label="Идентификатор объекта"
                placeholder="id объекта"
                className="h-9 w-40 rounded-sm border border-line bg-panel-2 py-1.5 pr-3 pl-8 font-mono text-sm text-ink transition-colors duration-140 placeholder:text-ink-faint hover:border-line-strong focus:border-honey/60"
              />
            </div>
            <DateRange filters={filters} onChange={setFilters} />
          </>
        }
      >
        {audit.isError ? (
          <div className="p-4">
            <ErrorBlock
              error={audit.error}
              onRetry={() => void audit.refetch()}
              title="Аудит не загрузился"
            />
          </div>
        ) : audit.isPending ? (
          <TableSkeleton />
        ) : rows.length === 0 ? (
          <div className="p-4">
            <EmptyBlock
              title={filtersActive ? 'Под фильтры ничего не попало' : 'Записей аудита пока нет'}
              note={
                filtersActive
                  ? 'Расширьте период или снимите часть условий.'
                  : 'Здесь появятся входы, изменения ботов, аккаунтов и серверов — с указанием, кто и откуда их сделал.'
              }
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <caption className="sr-only">
                  Журнал событий безопасности организации: время, действие, актор, объект и адрес
                </caption>
                <thead>
                  <tr className="border-b border-line bg-panel-3/60">
                    <th scope="col" className="w-8" />
                    <th
                      scope="col"
                      aria-sort={sortDesc ? 'descending' : 'ascending'}
                      className="px-2 py-2 font-medium text-ink-muted"
                    >
                      <button
                        type="button"
                        onClick={() => setSortDesc(previous => !previous)}
                        className="press inline-flex items-center gap-1 rounded-xs text-ink-muted hover:text-ink"
                      >
                        Время
                        <ChevronDown
                          aria-hidden
                          className={cn(
                            'size-3.5 transition-transform duration-140',
                            sortDesc ? '' : 'rotate-180'
                          )}
                        />
                      </button>
                    </th>
                    <Th>Действие</Th>
                    <Th>Кто</Th>
                    <Th>Объект</Th>
                    <Th>Откуда</Th>
                  </tr>
                </thead>

                {sorted.map(entry => (
                  <AuditRow
                    key={entry.id}
                    entry={entry}
                    open={expanded === entry.id}
                    onToggle={() =>
                      setExpanded(previous => (previous === entry.id ? null : entry.id))
                    }
                  />
                ))}
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
              <p className="text-2xs text-ink-faint">
                Сортировка применяется к загруженным записям: API отдаёт аудит от новых к старым.
              </p>
              {audit.hasNextPage ? (
                <Button
                  onClick={() => void audit.fetchNextPage()}
                  loading={audit.isFetchingNextPage}
                >
                  Показать ещё {PAGE_LIMIT}
                </Button>
              ) : (
                <span className="text-2xs text-ink-faint">Это все записи по фильтру</span>
              )}
            </div>
          </>
        )}
      </Panel>
    </>
  )
}

/** Фильтры → параметры `GET /audit`. Дата превращается в границы суток. */
export function toQuery(filters: Filters): AuditQuery {
  return {
    limit: PAGE_LIMIT,
    ...(filters.action === '' ? {} : { action: filters.action }),
    ...actorFilterToQuery(filters.actor),
    ...(filters.targetType === '' ? {} : { targetType: filters.targetType }),
    ...(filters.targetId === '' ? {} : { targetId: filters.targetId }),
    // `from` — начало выбранного дня, `to` — его конец: иначе выбор одного и того
    // же дня в обоих полях давал бы пустую выборку.
    ...(filters.from === '' ? {} : { from: `${filters.from}T00:00:00.000Z` }),
    ...(filters.to === '' ? {} : { to: `${filters.to}T23:59:59.999Z` })
  }
}

function Th({ children }: { children: string }): JSX.Element {
  return (
    <th scope="col" className="px-2 py-2 font-medium text-ink-muted">
      {children}
    </th>
  )
}

function DateRange({
  filters,
  onChange
}: {
  filters: Filters
  onChange: (update: (previous: Filters) => Filters) => void
}): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="date"
        value={filters.from}
        max={filters.to === '' ? undefined : filters.to}
        onChange={event => onChange(previous => ({ ...previous, from: event.target.value }))}
        aria-label="Дата начала периода"
        className="h-9 rounded-sm border border-line bg-panel-2 px-2 font-mono text-sm text-ink hover:border-line-strong focus:border-honey/60"
      />
      <span aria-hidden className="text-ink-faint">
        —
      </span>
      <input
        type="date"
        value={filters.to}
        min={filters.from === '' ? undefined : filters.from}
        onChange={event => onChange(previous => ({ ...previous, to: event.target.value }))}
        aria-label="Дата конца периода"
        className="h-9 rounded-sm border border-line bg-panel-2 px-2 font-mono text-sm text-ink hover:border-line-strong focus:border-honey/60"
      />
    </div>
  )
}

/**
 * Строка аудита. `tbody` на запись, а не `tr`: подробности живут отдельной
 * строкой таблицы, и оба `tr` должны быть в одной группе — иначе разметка
 * ломается, стоит включить сортировку или зебру.
 */
function AuditRow({
  entry,
  open,
  onToggle
}: {
  entry: AuditEntryDto
  open: boolean
  onToggle: () => void
}): JSX.Element {
  const actor = auditActor(entry)
  const tone = auditActionTone(entry.action)
  const detailsId = `audit-meta-${entry.id}`

  return (
    <tbody className="border-b border-line/70 align-top last:border-b-0 hover:bg-panel-2/50">
      <tr>
        <td className="py-2 pl-2">
          <IconButton
            label={open ? 'Скрыть подробности' : 'Показать подробности'}
            size="sm"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={onToggle}
          >
            {open ? (
              <ChevronDown aria-hidden className="size-4" />
            ) : (
              <ChevronRight aria-hidden className="size-4" />
            )}
          </IconButton>
        </td>

        <td className="px-2 py-2 font-mono text-xs whitespace-nowrap text-ink-muted tabular-nums">
          <span className="text-ink">{clockOf(entry.at)}</span>
          <span className="ml-2 text-ink-faint">{dateTimeOf(entry.at)?.split(',')[0] ?? ''}</span>
        </td>

        <td className="px-2 py-2">
          <span className="flex items-center gap-2">
            {tone === null ? null : <StatusDot tone={tone} />}
            <span className="text-ink">{auditActionLabel(entry.action)}</span>
            {isKnownAuditAction(entry.action) ? null : (
              <Chip tone="neutral" title="Код действия ещё не переведён на этой панели">
                новый код
              </Chip>
            )}
          </span>
          {/* Сырой код рядом с названием: именно его грепают в логах сервера. */}
          <span className="mt-0.5 block font-mono text-2xs text-ink-faint">{entry.action}</span>
        </td>

        <td className="px-2 py-2">
          <span className="block text-ink">{actor.label}</span>
          {actor.note === null ? null : (
            <span className="mt-0.5 block truncate text-2xs text-ink-faint" title={actor.note}>
              {actor.note}
            </span>
          )}
        </td>

        <td className="px-2 py-2">
          <span className="block text-ink-muted">{targetTypeLabel(entry.targetType)}</span>
          {entry.targetId === null ? null : (
            <span className="mt-0.5 block font-mono text-2xs text-ink-faint">{entry.targetId}</span>
          )}
        </td>

        <td className="max-w-56 px-2 py-2 pr-3">
          <span className="block font-mono text-xs text-ink-muted">{entry.ip ?? '—'}</span>
          {entry.userAgent === null ? null : (
            <span className="mt-0.5 block truncate text-2xs text-ink-faint" title={entry.userAgent}>
              {entry.userAgent}
            </span>
          )}
        </td>
      </tr>

      {open ? (
        <tr id={detailsId}>
          <td colSpan={6} className="px-4 pt-0 pb-3">
            <MetaBlock entry={entry} />
          </td>
        </tr>
      ) : null}
    </tbody>
  )
}

function MetaBlock({ entry }: { entry: AuditEntryDto }): JSX.Element {
  const json = useMemo(() => stringifyMeta(entry.meta), [entry.meta])

  return (
    <div className="anim-fade rounded-sm border border-line bg-abyss/50 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-3 text-2xs text-ink-faint">
        <span>
          Полное время: <span className="font-mono text-ink-muted">{entry.at}</span>
        </span>
        <span>
          Запись: <span className="font-mono text-ink-muted">#{entry.id}</span>
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto"
          onClick={() => void copy(json)}
          disabled={json === null}
        >
          <Copy aria-hidden className="size-3.5" />
          Скопировать JSON
        </Button>
      </div>
      {json === null ? (
        <p className="text-2xs text-ink-muted">
          Подробностей нет: действие не приложило метаданных.
        </p>
      ) : (
        <pre className="max-h-64 overflow-auto font-mono text-xs whitespace-pre text-ink-muted">
          {json}
        </pre>
      )}
    </div>
  )
}

/** `meta` — свободный JSON из хендлера; циклы и `undefined` не должны ронять экран. */
function stringifyMeta(meta: unknown): string | null {
  if (meta === null || meta === undefined) return null
  if (typeof meta === 'object' && Object.keys(meta as object).length === 0) return null
  try {
    return JSON.stringify(meta, null, 2)
  } catch {
    return String(meta)
  }
}

async function copy(text: string | null): Promise<void> {
  if (text === null) return
  try {
    await navigator.clipboard.writeText(text)
    toast('JSON скопирован', 'success')
  } catch {
    // Буфер обмена может быть запрещён политикой браузера — это не ошибка экрана.
    toast('Браузер не дал доступ к буферу обмена', 'warning')
  }
}

/** Скелетон в форме таблицы: строки той же высоты, что настоящие. */
function TableSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-2 p-4" aria-hidden>
      <span className="sr-only">Загрузка аудита</span>
      {Array.from({ length: 8 }, (_unused, index) => (
        <div key={index} className="shimmer h-11 rounded-sm bg-panel-2" />
      ))}
    </div>
  )
}
